import { yupResolver } from "@hookform/resolvers/yup";
import { Language } from "@omni/schemas/config/language.schema";
import { ComponentsCustomer } from "@omni/types/components/customer/customer.model";
import { Locale } from "@omni/types/enums/locale.enum";
import { values } from "@omni/utils/object.utils";
import { Suspense } from "react";
import { FormProvider } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";

import { SelectField } from "@/components/Form/RHF/FormFields/SelectField/selectField.component";
import { StoreField } from "@/components/Form/RHF/FormFields/StoreField/storeField.container";
import { StoreSchema } from "@/components/Form/RHF/FormFields/StoreField/storeField.schema";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import { getShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

const EditPreferencesFormValidationSchema = yup
    .object({
        locale: yup.mixed<Locale>().oneOf(values(Locale)).required(),
        favouriteStoreId: yup.string().required()
    })
    .concat(StoreSchema);

type FormValues = yup.InferType<typeof EditPreferencesFormValidationSchema>;

type LanguageOption = {
    label: string;
    value: NonNullable<Locale | undefined>;
};

type Props = {
    languages: Language[];
    customer: ComponentsCustomer;
    onSubmit: (language: Locale, favouriteStoreId?: string) => void;
};

const FORM_ID = "profileEditPreferencesForm";

const EditPreferencesForm = ({ onSubmit, customer, languages }: Props) => {
    const { t } = useGranite();

    const shopNames = useSelector(getShopNames, shallowEqual);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            locale: customer.language,
            favouriteStoreId: customer.favouriteStoreId || ""
        },
        resolver: yupResolver(EditPreferencesFormValidationSchema)
    });

    const onFormSubmit = ({ locale, favouriteStoreId }: FormValues) => {
        onSubmit?.(locale, favouriteStoreId);
    };

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            <div>
                <Messages messageid={"edit-preferences-failure"} />
                <FormInfoProvider
                    formId={FORM_ID}
                    withNeverbounce={false}
                >
                    {() => (
                        <FormProvider {...methods}>
                            <form
                                id={FORM_ID}
                                onSubmit={methods.handleSubmit(onFormSubmit)}
                            >
                                <Container>
                                    <Row>
                                        <Col>
                                            <SelectField<
                                                FormValues,
                                                LanguageOption
                                            >
                                                name="locale"
                                                label={t(
                                                    "general.form.language.label"
                                                )}
                                                placeholder={t(
                                                    "general.form.language.placeholder"
                                                )}
                                                autoComplete={"off"}
                                                required={true}
                                                optionList={languages.map(
                                                    (l) => ({
                                                        label: l[
                                                            "language-name"
                                                        ],
                                                        value: l.locale
                                                    })
                                                )}
                                            />
                                            <StoreField<FormValues>
                                                name="favouriteStoreId"
                                                label={t(
                                                    "general.form.store.label"
                                                )}
                                                placeholder={t(
                                                    "general.form.store.placeholder"
                                                )}
                                                required
                                                storeList={shopNames}
                                                optionalIndication={true}
                                            />
                                        </Col>
                                    </Row>
                                </Container>
                            </form>
                        </FormProvider>
                    )}
                </FormInfoProvider>
            </div>
        </Suspense>
    );
};

export default EditPreferencesForm;
